import { useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { motion } from "framer-motion";

// Define image paths with content
const processImages = [{
  name: "CV parsing",
  image: "/lovable-uploads/c2caee5b-28bf-4fc8-8da1-a9517cb82e95.png",
  content: "Si comincia da un'estrazione precisa e puntuale di tutte le informazioni chiave contenute nei documenti dei candidati, presentati al recruiter sotto forma di riassunto.\n\nTrasformiamo dati non strutturati in dati strutturati, garantendo un'azione priva di bias. Il nostro sistema non è influenzato da variabili che non influiscono sulle capacità del candidato (come indirizzi, dati anagrafici, foto del cv)."
}, {
  name: "Analisi CV",
  image: "/lovable-uploads/3f2e45bf-a59c-4b44-8f93-7c773c874505.png",
  content: "Cv e Cover Letter sono analizzati in modo innovativo:\n\n• Analisi di parole chiave e dei percorsi di carriera, con attenzione anche sui casi più atipici. Secondo McKinsey & Co. è fondamentale andare a ricercare competenze anche in percorsi considerati \"diversi\". Noi lo facciamo.\n\n• Estrazione gap e punti di forza rispetto alla posizione lavorativa di riferimento, e alle skill generalmente richieste dal mercato per la posizione. Così aiutiamo anche i recruiter a capire se i requisiti richiesti sono in linea o meno con il mercato."
}, {
  name: "Test automatici",
  image: "/lovable-uploads/cd23a827-2be0-4708-aac1-871e0408f8e7.png",
  content: "In caso di incertezza sui gap individuati, il nostro Agente AI potrà provvedere a una verifica con il candidato, erogando in autonomia un test personalizzato e interattivo. Si occuperà lui di notificare, erogare, e analizzare i risultati del test.\n\nQuesta metodologia permette di offrire a tutti i candidati un'opportunità, aumentando ancora di più la trasparenza del processo."
}, {
  name: "Esperienza su misura",
  image: "/lovable-uploads/71e821d3-113f-4747-bb28-d4be8fe70c91.png",
  content: "Offriamo un percorso di candidatura personalizzato per ogni candidato:\n\n• Test ad-hoc sulla base dei GAP identificati dalla documentazione di candidatura\n\n• Eventuale email di rifiuto che giustifichi la decisione sulla base delle evidenze e suggerisca percorsi di formazione per ottenere le skill necessarie per il lavoro"
}, {
  name: "Tecnologia proprietaria",
  image: "/lovable-uploads/0db8d4d9-5344-4237-a8ea-647aedd677c3.png",
  content: "La nostra tecnologia proprietaria garantisce la massima sicurezza dei dati e il controllo completo del processo."
}];

const partners = [{
  name: "Partner 1",
  logo: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
}, {
  name: "Partner 2",
  logo: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"
}, {
  name: "Partner 3",
  logo: "https://images.unsplash.com/photo-1518770660439-4636190af475"
}, {
  name: "Partner 4",
  logo: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6"
}, {
  name: "Partner 5",
  logo: "https://images.unsplash.com/photo-1531297484001-80022131f5a1"
}];

const ImageDialog = ({
  image,
  name,
  content
}: {
  image: string;
  name: string;
  content: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <motion.div className="cursor-pointer relative" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} initial={{
        rotateY: 0
      }} animate={isOpen ? {
        rotateY: 180
      } : {
        rotateY: 0
      }} transition={{
        duration: 0.6
      }}>
          <img src={image} alt={name} className="w-48 h-48 object-contain" />
        </motion.div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <motion.div initial={{
        opacity: 0,
        y: 20,
        rotateY: -180
      }} animate={{
        opacity: 1,
        y: 0,
        rotateY: 0
      }} transition={{
        duration: 0.4,
        delay: 0.3
      }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold mb-2">{name}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-secondary text-base whitespace-pre-line">{content}</p>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>;
};

const Partners = () => {
  const component = useRef(null);
  const partnersRef = useRef<(HTMLDivElement | null)[]>([]);
  const textRef = useRef(null);
  const bulletPointsRef = useRef(null);
  const additionalTextRef = useRef(null);
  const finalTextRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      partnersRef.current.forEach((partner, index) => {
        gsap.from(partner, {
          scrollTrigger: {
            trigger: partner,
            start: "top bottom-=100",
            toggleActions: "play none none reverse"
          },
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: index * 0.2,
          ease: "power3.out"
        });
      });
      if (textRef.current) {
        gsap.from(textRef.current, {
          scrollTrigger: {
            trigger: textRef.current,
            start: "top bottom-=50",
            toggleActions: "play none none reverse"
          },
          x: 30,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out"
        });
      }
      if (bulletPointsRef.current) {
        gsap.from(bulletPointsRef.current, {
          scrollTrigger: {
            trigger: bulletPointsRef.current,
            start: "top bottom-=30",
            toggleActions: "play none none reverse"
          },
          y: 30,
          opacity: 0,
          duration: 0.8,
          delay: 0.4,
          ease: "power3.out"
        });
      }
      if (additionalTextRef.current) {
        gsap.from(additionalTextRef.current, {
          scrollTrigger: {
            trigger: additionalTextRef.current,
            start: "top bottom-=20",
            toggleActions: "play none none reverse"
          },
          y: 30,
          opacity: 0,
          duration: 0.8,
          delay: 0.6,
          ease: "power3.out"
        });
      }
      if (finalTextRef.current) {
        gsap.from(finalTextRef.current, {
          scrollTrigger: {
            trigger: finalTextRef.current,
            start: "top bottom-=20",
            toggleActions: "play none none reverse"
          },
          y: 30,
          opacity: 0,
          duration: 0.8,
          delay: 0.8,
          ease: "power3.out"
        });
      }
    }, component);
    return () => ctx.revert();
  }, []);

  return null;
};

export default Partners;
